import {
  App,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { feeApi, type FeeFrequency, type FeeType } from '@/api/fee';
import { financeApi } from '@/api/finance';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const FREQUENCY_LABEL: Record<FeeFrequency, string> = {
  MONTHLY: 'Monthly',
  ONE_TIME: 'One-time',
  PER_TERM: 'Per term',
};
const FREQUENCY_COLOR: Record<FeeFrequency, string> = {
  MONTHLY: 'processing',
  ONE_TIME: 'success',
  PER_TERM: 'warning',
};

const QK = ['fee', 'types'] as const;

interface FormShape {
  name: string;
  frequency: FeeFrequency;
  incomeAccountId: number;
}

export function FeeTypesPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('FEE_MGMT:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('FEE_MGMT:EDIT'));

  const [editing, setEditing] = useState<FeeType | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data, isLoading } = useQuery({
    queryKey: QK,
    queryFn: () => feeApi.listTypes(false),
  });

  const accountsQuery = useQuery({
    queryKey: ['finance', 'accounts', 'income-leaves'],
    queryFn: () => financeApi.listAccounts(),
    staleTime: 5 * 60 * 1000,
  });
  const incomeAccounts = (accountsQuery.data ?? []).filter(
    (a) => a.accountType === 'INCOME' && a.postingAccount && a.active,
  );

  const upsert = useMutation({
    mutationFn: (values: FormShape) =>
      editing
        ? feeApi.updateType(editing.id, values)
        : feeApi.createType(values),
    onSuccess: () => {
      message.success(editing ? 'Fee type updated' : 'Fee type created');
      qc.invalidateQueries({ queryKey: QK });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) => feeApi.toggleType(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ frequency: 'MONTHLY' });
  };
  const openEdit = (row: FeeType) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      frequency: row.frequency,
      incomeAccountId: row.incomeAccountId,
    });
  };

  const open = creating || editing != null;

  const columns: TableProps<FeeType>['columns'] = [
    { title: 'Name', dataIndex: 'name', render: (n: string) => <span style={{ fontWeight: 600 }}>{n}</span> },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      width: 160,
      render: (f: FeeFrequency) => <Tag color={FREQUENCY_COLOR[f]}>{FREQUENCY_LABEL[f]}</Tag>,
    },
    {
      title: 'Posts to',
      width: 220,
      render: (_, row) =>
        row.incomeAccountCode ? (
          <Tooltip title="Open chart of accounts">
            <Link
              to="/finance/accounts"
              style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}
            >
              {row.incomeAccountCode} · {row.incomeAccountName}
            </Link>
          </Tooltip>
        ) : (
          <Tag>—</Tag>
        ),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (active: boolean, row) => (
        <Switch
          size="small"
          checked={active}
          disabled={!canEdit}
          onChange={(v) => toggle.mutate({ id: row.id, value: v })}
        />
      ),
    },
    {
      title: 'Actions',
      width: 110,
      align: 'right',
      render: (_, row) =>
        canEdit ? (
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Fee"
        title="Fee types"
        description="Catalog of billable fees. Frequency drives whether monthly invoices auto-generate."
        count={{ value: data?.length ?? 0, label: 'types' }}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New fee type
            </Button>
          ) : null
        }
      />

      <Table
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={data ?? []}
        pagination={false}
      />

      <Modal
        title={editing ? `Edit ${editing.name}` : 'New fee type'}
        open={open}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Create'}
        destroyOnHidden
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(values) => upsert.mutate(values)}>
            <Form.Item label="Name" name="name" rules={[{ required: true, max: 100 }]}>
              <Input autoFocus placeholder="e.g. Tuition" />
            </Form.Item>
            <Form.Item label="Frequency" name="frequency" rules={[{ required: true }]}>
              <Select
                options={(Object.keys(FREQUENCY_LABEL) as FeeFrequency[]).map((f) => ({
                  value: f,
                  label: FREQUENCY_LABEL[f],
                }))}
              />
            </Form.Item>
            <Form.Item
              label="Posts to (GL income account)"
              name="incomeAccountId"
              rules={[{ required: true, message: 'Required — drives the trial balance posting' }]}
              tooltip="When a payment for this fee type is collected, the receipt credits this income account."
            >
              <Select
                showSearch
                optionFilterProp="label"
                loading={accountsQuery.isLoading}
                placeholder="Select an income account"
                options={incomeAccounts.map((a) => ({
                  value: a.id,
                  label: `${a.code} · ${a.name}`,
                }))}
              />
            </Form.Item>
            <Space size="small" wrap>
              <span style={{ fontSize: 12, opacity: 0.6 }}>Tip:</span>
              <Tag color="processing">Monthly</Tag>
              <span style={{ fontSize: 12 }}>auto-generates per month;</span>
              <Tag color="success">One-time</Tag>
              <span style={{ fontSize: 12 }}>and</span>
              <Tag color="warning">Per term</Tag>
              <span style={{ fontSize: 12 }}>are added manually.</span>
            </Space>
          </Form>
        )}
      </Modal>
    </div>
  );
}
